import { useNavigate } from "react-router-dom";
import chinaRegions from "./chinaRegions.json";
import { useEffect, useMemo, useState } from "react";
import { MARITAL_STATUS_OPTIONS, getProfileCompleteRedirectPath, getMaritalStatusChangeMessage, syncMaritalStatus } from "../constants/maritalStatus";
import { resolveRegionFromDb } from "../utils/resolveRegion";
import { assetUrl } from "../utils/api";

export default function ProfileSetup() {
  const navigate = useNavigate();
  const currentUserId = Number(localStorage.getItem("user_id"));

  const [loading, setLoading] = useState(false);
  const [avatar, setAvatar] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [photoUrls, setPhotoUrls] = useState([]);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [initialMaritalStatus, setInitialMaritalStatus] = useState("");
  const [ageLocked, setAgeLocked] = useState(false);
  const [lockedBirth, setLockedBirth] = useState(null);
  const [birthLockError, setBirthLockError] = useState("");
  const [isIdVerified, setIsIdVerified] = useState(false);
  const [photoPreviews, setPhotoPreviews] = useState([]);

  const [form, setForm] = useState({
    name: "",
    gender: "",
    birthYear: "",
    birthMonth: "",
    birthDay: "",
    province: "",
    city: "",
    district: "",
    marital_status: "",
    education: "",
    job: "",
    height: "",
    weight: "",
    monthly_income: "",
    housing_status: "",
    religion: "",
    bio: "",
    hobbies: "",
    partner_requirement: "",
  });

  const inputClass =
    "w-full rounded-xl border border-rose-100 bg-rose-50/40 px-4 py-3 text-sm text-gray-700 placeholder:text-gray-400 outline-none transition focus:border-pink-400 focus:bg-white focus:ring-2 focus:ring-pink-100";

  const sectionTitleClass = "text-sm font-semibold text-gray-700";
  const pageTitleClass = "text-3xl font-bold tracking-tight text-gray-800";

  const provinces = chinaRegions;

  const cities = useMemo(() => {
    const province = provinces.find((item) => item.name === form.province);
    return province?.children || [];
  }, [form.province, provinces]);

  const districts = useMemo(() => {
    const city = cities.find((item) => item.name === form.city);
    return city?.children || [];
  }, [form.city, cities]);

  const today = new Date();

  const maxBirthDate = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );

  const maxYear = maxBirthDate.getFullYear();
  const maxMonth = maxBirthDate.getMonth() + 1;
  const maxDay = maxBirthDate.getDate();

  const years = useMemo(() => {
    const arr = [];
    for (let year = maxYear; year >= 1950; year -= 1) {
      arr.push(year);
    }
    return arr;
  }, [maxYear]);

  const months = useMemo(() => {
    if (!form.birthYear) return [];
    const selectedYear = Number(form.birthYear);
    const endMonth = selectedYear === maxYear ? maxMonth : 12;
    return Array.from({ length: endMonth }, (_, i) => i + 1);
  }, [form.birthYear, maxMonth, maxYear]);

  const days = useMemo(() => {
    if (!form.birthYear || !form.birthMonth) return [];
    const year = Number(form.birthYear);
    const month = Number(form.birthMonth);
    let endDay = new Date(year, month, 0).getDate();
    if (year === maxYear && month === maxMonth) {
      endDay = Math.min(endDay, maxDay);
    }
    return Array.from({ length: endDay }, (_, i) => i + 1);
  }, [form.birthYear, form.birthMonth, maxDay, maxMonth, maxYear]);

  const calculateAge = (year, month, day) => {
    const birth = new Date(Number(year), Number(month) - 1, Number(day));
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age -= 1;
    }
    return age;
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return "";
    const formData = new FormData();
    formData.append("user_id", String(currentUserId));
    formData.append("file", avatarFile);

    const res = await fetch("/api/profile/upload-avatar", {
      method: "POST",
      body: formData,
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "头像上传失败");

    const fullUrl = data.avatar ? `${data.avatar}` : "";
    setAvatarUrl(fullUrl);
    localStorage.setItem("avatar", fullUrl);
    window.dispatchEvent(new Event("avatarUpdated"));
    return data.avatar;
  };

  const uploadPhotos = async () => {
    if (!photoFiles.length) return [];
    const formData = new FormData();
    formData.append("user_id", String(currentUserId));
    photoFiles.forEach((file) => formData.append("files", file));

    const res = await fetch("/api/profile/upload-photos", {
      method: "POST",
      body: formData,
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "生活照上传失败");

    const fullUrls = (data.photos || []).map((photo) => {
      if (typeof photo === "string") return photo;
      if (photo?.image_url) return photo.image_url;
      return "";
    }).filter(Boolean);

    setPhotoUrls(fullUrls);
    return data.photos;
  };

  useEffect(() => {
    if (!currentUserId) {
      navigate("/login");
      return;
    }

    const loadProfile = async () => {
      try {
        const res = await fetch(`/api/profile/${currentUserId}`);
        const data = await res.json();
        if (!res.ok) return;

        setAgeLocked(Boolean(data.age_locked));
        setIsIdVerified(Boolean(data.is_id_verified));
        if (data.age_locked) {
          setLockedBirth({
            birth_year: data.birth_year,
            birth_month: data.birth_month,
            birth_day: data.birth_day,
            age: data.age,
          });
        } else {
          setLockedBirth(null);
        }
        setBirthLockError("");

        const region = resolveRegionFromDb(
          data.province,
          data.city,
          data.district,
          provinces
        );

        setAvatar(data.avatar ? `${data.avatar}` : "");

        const fullUrl = data.avatar ? `${data.avatar}` : "";
        setAvatarUrl(fullUrl);
        localStorage.setItem("avatar", fullUrl);
        window.dispatchEvent(new Event("avatarUpdated"));

        setForm((prev) => ({
          ...prev,
          name: data.name || "",
          gender: data.gender || "",
          birthYear: data.birth_year ? String(data.birth_year) : "",
          birthMonth: data.birth_month ? String(data.birth_month) : "",
          birthDay: data.birth_day ? String(data.birth_day) : "",
          province: region.province,
          city: region.city,
          district: region.district,
          marital_status: data.marital_status || "",
          education: data.education || "",
          job: data.job || "",
          height: data.height ? String(data.height) : "",
          weight: data.weight ? String(data.weight) : "",
          monthly_income: data.monthly_income || "",
          housing_status: data.housing_status || "",
          religion: data.religion || "",
          bio: data.bio || "",
          hobbies: data.hobbies || "",
          partner_requirement: data.partner_requirement || "",
        }));
        setInitialMaritalStatus(data.marital_status || "");
        syncMaritalStatus(data.marital_status || "");

        const photoArray = (data.photos || []).map((photo) => {
          if (typeof photo === "string") return photo;
          if (photo?.image_url) return photo.image_url;
          return "";
        }).filter(Boolean);

        setPhotoUrls(photoArray);
      } catch (err) {
        console.error("加载资料失败:", err);
      }
    };

    loadProfile();
  }, [currentUserId, navigate]);

  const [formErrors, setFormErrors] = useState({});

  const getMissingRequired = (values = form) => {
    const missing = [];
    if (!String(values.name || "").trim()) missing.push({ key: "name", label: "姓名" });
    if (!values.gender) missing.push({ key: "gender", label: "性别" });
    if (!values.birthYear) missing.push({ key: "birthYear", label: "出生年" });
    if (!values.birthMonth) missing.push({ key: "birthMonth", label: "出生月" });
    if (!values.birthDay) missing.push({ key: "birthDay", label: "出生日" });
    if (!values.province) missing.push({ key: "province", label: "省份" });
    if (!values.city) missing.push({ key: "city", label: "城市" });
    if (!values.district) missing.push({ key: "district", label: "区县" });
    return missing;
  };

  const fieldClass = (key) =>
    `${inputClass}${formErrors[key] ? " border-red-400 focus:border-red-400 focus:ring-red-100" : ""}`;

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setFormErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => {
        delete next[key];
      });
      return next;
    });
  };

  const liveMissing = getMissingRequired();

  const handleSave = async () => {
    if (!currentUserId) {
      alert("请先登录");
      navigate("/login");
      return;
    }

    const missing = getMissingRequired();
    if (missing.length) {
      const nextErrors = {};
      missing.forEach((item) => {
        nextErrors[item.key] = true;
      });
      setFormErrors(nextErrors);
      alert(`请先完善以下必填项：${missing.map((m) => m.label).join("、")}`);
      return;
    }
    setFormErrors({});

    const age = calculateAge(form.birthYear, form.birthMonth, form.birthDay);
    if (age < 18) {
      alert("仅支持18周岁及以上用户");
      return;
    }

    setLoading(true);
    setBirthLockError("");
    try {
      await uploadAvatar();
      await uploadPhotos();

      const birthPayload =
        ageLocked && lockedBirth
          ? {
              birth_year: lockedBirth.birth_year,
              birth_month: lockedBirth.birth_month,
              birth_day: lockedBirth.birth_day,
              age: lockedBirth.age ?? age,
            }
          : {
              birth_year: Number(form.birthYear),
              birth_month: Number(form.birthMonth),
              birth_day: Number(form.birthDay),
              age,
            };

      const res = await fetch("/api/profile/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: currentUserId,
          name: form.name,
          gender: form.gender,
          ...birthPayload,
          province: form.province,
          city: form.city,
          district: form.district,
          marital_status: form.marital_status,
          education: form.education,
          job: form.job,
          height: form.height ? Number(form.height) : null,
          weight: form.weight ? Number(form.weight) : null,
          monthly_income: form.monthly_income,
          housing_status: form.housing_status,
          religion: form.religion,
          bio: form.bio,
          hobbies: form.hobbies,
          partner_requirement: form.partner_requirement,
          photos: photoUrls,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail || "资料保存失败";
        if (String(detail).includes("出生日期已锁定")) {
          setBirthLockError(detail);
          return;
        }
        alert(detail);
        return;
      }

      const nextStatus = data.user?.marital_status || form.marital_status;
      const changeMsg = getMaritalStatusChangeMessage(
        initialMaritalStatus,
        nextStatus
      );

      syncMaritalStatus(nextStatus);
      setInitialMaritalStatus(nextStatus);
      setAgeLocked(true);

      alert(changeMsg ? `资料保存成功。${changeMsg}` : "资料保存成功");

      if (!data.user?.profile_completed) {
        navigate("/center/profile");
      } else if (changeMsg) {
        navigate("/center");
      } else {
        navigate(getProfileCompleteRedirectPath(nextStatus, data.user || {}));
      }
    } catch (err) {
      console.error("保存失败:", err);
      alert(err.message || "保存失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="max-w-2xl mx-auto rounded-3xl bg-white/95 shadow-[0_20px_60px_rgba(236,72,153,0.08)] p-6 md:p-8 space-y-6">
        <div className="text-center space-y-2">
          <h2 className={pageTitleClass}>完善资料</h2>
          <p className="text-sm text-gray-400">让大家先看到更完整、更真实的你</p>
          <p className="text-xs text-gray-400">
            带 <span className="text-pink-500">*</span> 为必填项
          </p>
        </div>

        {liveMissing.length > 0 ? (
          <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-800 leading-7">
            <p className="font-medium">还差以下必填项未完善：</p>
            <p className="mt-1">{liveMissing.map((m) => m.label).join("、")}</p>
          </div>
        ) : (
          <div className="rounded-xl bg-green-50 border border-green-100 px-4 py-3 text-sm text-green-700">
            必填项已齐全，可保存；完善更多资料可解锁查看对方对应信息。
          </div>
        )}

        {/* 头像 */}
        <div className="space-y-3">
          <p className={sectionTitleClass}>头像</p>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setAvatarFile(file);
              setAvatarPreview(file ? URL.createObjectURL(file) : "");
            }}
            className={inputClass}
          />
          {(avatarPreview || avatarUrl) && (
            <img
              src={avatarPreview || assetUrl(avatarUrl)}
              alt="头像"
              className="w-24 h-24 rounded-full object-cover border border-rose-100"
            />
          )}
        </div>

        {/* 生活照 */}
        <div className="space-y-3">
          <p className={sectionTitleClass}>生活照</p>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              setPhotoFiles(files);
              setPhotoPreviews(files.map((file) => URL.createObjectURL(file)));
            }}
            className={inputClass}
          />
          {(photoPreviews.length > 0 || photoUrls.length > 0) && (
            <div className="grid grid-cols-3 gap-3">
              {(photoPreviews.length > 0 ? photoPreviews : photoUrls)
                .filter(Boolean)
                .map((url, index) => {
                  const displaySrc =
                    url.startsWith("blob:") ? url : assetUrl(url);
                  return (
                    <img
                      key={index}
                      src={displaySrc}
                      alt={`生活照${index + 1}`}
                      className="w-full h-24 object-cover rounded-xl border border-rose-100"
                    />
                  );
                })}
            </div>
          )}
        </div>

        {/* 基本信息 */}
        <div className="space-y-1">
          <p className={sectionTitleClass}>
            姓名 <span className="text-pink-500">*</span>
          </p>
          <input
            placeholder="请填写姓名"
            value={form.name}
            onChange={(e) => updateForm({ name: e.target.value })}
            className={fieldClass("name")}
          />
          {formErrors.name ? (
            <p className="text-xs text-red-500">请填写姓名</p>
          ) : null}
        </div>
        <div className="space-y-1">
          <p className={sectionTitleClass}>
            性别 <span className="text-pink-500">*</span>
          </p>
          <select
            value={form.gender}
            onChange={(e) => updateForm({ gender: e.target.value })}
            className={fieldClass("gender")}
          >
            <option value="">请选择性别</option>
            <option value="男">男</option>
            <option value="女">女</option>
          </select>
          {formErrors.gender ? (
            <p className="text-xs text-red-500">请选择性别</p>
          ) : null}
        </div>

        {/* 出生日期 */}
        <div className="space-y-2">
          <p className={sectionTitleClass}>
            出生日期 <span className="text-pink-500">*</span>
          </p>
          {!ageLocked ? (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              请仔细填写出生日期，保存后将无法自行修改。若填错，需前往「认证中心」上传身份证申请更正。
            </p>
          ) : (
            <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
              出生日期已锁定
              {isIdVerified ? "（已通过身份证认证）" : "，如需修改请前往「认证中心」上传身份证"}
            </p>
          )}
          {birthLockError ? (
            <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {birthLockError}
            </p>
          ) : null}
          <div className="grid grid-cols-3 gap-2">
          <select
            value={form.birthYear}
            onChange={(e) =>
              updateForm({ birthYear: e.target.value, birthMonth: "", birthDay: "" })
            }
            className={fieldClass("birthYear")}
            disabled={ageLocked}
          >
            <option value="">出生年</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}年
              </option>
            ))}
          </select>
          <select
            value={form.birthMonth}
            onChange={(e) =>
              updateForm({ birthMonth: e.target.value, birthDay: "" })
            }
            className={fieldClass("birthMonth")}
            disabled={!form.birthYear || ageLocked}
          >
            <option value="">出生月</option>
            {months.map((month) => (
              <option key={month} value={month}>
                {month}月
              </option>
            ))}
          </select>
          <select
            value={form.birthDay}
            onChange={(e) => updateForm({ birthDay: e.target.value })}
            className={fieldClass("birthDay")}
            disabled={!form.birthYear || !form.birthMonth || ageLocked}
          >
            <option value="">出生日</option>
            {days.map((day) => (
              <option key={day} value={day}>
                {day}日
              </option>
            ))}
          </select>
          </div>
          {(formErrors.birthYear || formErrors.birthMonth || formErrors.birthDay) && (
            <p className="text-xs text-red-500">请完整选择出生年、月、日</p>
          )}
        </div>

        {/* 地址 */}
        <div className="space-y-2">
          <p className={sectionTitleClass}>
            所在地区 <span className="text-pink-500">*</span>
          </p>
          <div className="grid grid-cols-3 gap-2">
          <select
            value={form.province}
            onChange={(e) =>
              updateForm({ province: e.target.value, city: "", district: "" })
            }
            className={fieldClass("province")}
          >
            <option value="">省</option>
            {provinces.map((province) => (
              <option key={province.code} value={province.name}>
                {province.name}
              </option>
            ))}
          </select>
          <select
            value={form.city}
            onChange={(e) => updateForm({ city: e.target.value, district: "" })}
            className={fieldClass("city")}
            disabled={!form.province}
          >
            <option value="">市</option>
            {cities.map((city) => (
              <option key={city.code} value={city.name}>
                {city.name}
              </option>
            ))}
          </select>
          <select
            value={form.district}
            onChange={(e) => updateForm({ district: e.target.value })}
            className={fieldClass("district")}
            disabled={!form.city}
          >
            <option value="">区/县</option>
            {districts.map((district) => (
              <option key={district.code} value={district.name}>
                {district.name}
              </option>
            ))}
          </select>
          </div>
          {(formErrors.province || formErrors.city || formErrors.district) && (
            <p className="text-xs text-red-500">请选择完整的省 / 市 / 区县</p>
          )}
        </div>

        {/* 其它字段 */}
        <div className="grid grid-cols-2 gap-3">
          <select
            value={form.marital_status}
            onChange={(e) => setForm({ ...form, marital_status: e.target.value })}
            className={inputClass}
          >
            <option value="">婚姻状态</option>
            {MARITAL_STATUS_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-2">
            修改婚姻状况后，个人中心会自动切换为对应服务后台
          </p>
          <select
            value={form.education}
            onChange={(e) => setForm({ ...form, education: e.target.value })}
            className={inputClass}
          >
            <option value="">学历</option>
            <option value="高中">高中</option>
            <option value="大专">大专</option>
            <option value="本科">本科</option>
            <option value="硕士">硕士</option>
            <option value="博士">博士</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <input
            placeholder="职业"
            value={form.job}
            onChange={(e) => setForm({ ...form, job: e.target.value })}
            className={inputClass}
          />
          <select
            value={form.monthly_income}
            onChange={(e) => setForm({ ...form, monthly_income: e.target.value })}
            className={inputClass}
          >
            <option value="">月收入</option>
            <option value="5k以下">5k以下</option>
            <option value="5k-10k">5k-10k</option>
            <option value="10k-20k">10k-20k</option>
            <option value="20k-50k">20k-50k</option>
            <option value="50k以上">50k以上</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <input
            placeholder="身高(cm)"
            type="number"
            value={form.height}
            onChange={(e) => setForm({ ...form, height: e.target.value })}
            className={inputClass}
          />
          <input
            placeholder="体重(kg)"
            type="number"
            value={form.weight}
            onChange={(e) => setForm({ ...form, weight: e.target.value })}
            className={inputClass}
          />
        </div>

        <select
          value={form.housing_status}
          onChange={(e) => setForm({ ...form, housing_status: e.target.value })}
          className={inputClass}
        >
          <option value="">住房情况</option>
          <option value="已购房">已购房</option>
          <option value="租房">租房</option>
          <option value="与家人同住">与家人同住</option>
          <option value="暂无住房">暂无住房</option>
        </select>

        <select
          value={form.religion}
          onChange={(e) => setForm({ ...form, religion: e.target.value })}
          className={inputClass}
        >
          <option value="">宗教信仰</option>
          <option value="未填写">未填写</option>
          <option value="无">无</option>
          <option value="基督教">基督教</option>
          <option value="天主教">天主教</option>
          <option value="东正教">东正教</option>
          <option value="佛教">佛教</option>
          <option value="伊斯兰教">伊斯兰教</option>
          <option value="道教">道教</option>
          <option value="其他">其他</option>
        </select>

        <textarea
          placeholder="自我介绍"
          value={form.bio}
          onChange={(e) => setForm({ ...form, bio: e.target.value })}
          className={`${inputClass} min-h-[110px]`}
        />
        <textarea
          placeholder="兴趣爱好"
          value={form.hobbies}
          onChange={(e) => setForm({ ...form, hobbies: e.target.value })}
          className={`${inputClass} min-h-[110px]`}
        />
        <textarea
          placeholder="择偶要求"
          value={form.partner_requirement}
          onChange={(e) => setForm({ ...form, partner_requirement: e.target.value })}
          className={`${inputClass} min-h-[110px]`}
        />

        <button
          onClick={handleSave}
          disabled={loading}
          className="w-full rounded-xl bg-pink-500 py-3 text-sm font-medium text-white transition hover:bg-pink-600 disabled:opacity-50"
        >
          {loading ? "保存中..." : "保存资料"}
        </button>
      </div>
    </div>
  );
}